
import { Box, Chip, Stack, Typography } from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import { formatDateOnly, labelSx, sectionTitleSx, valueSx } from "./styles";

// Helper: strip null/"null"/"undefined"/"N/A" strings so cards never show them.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const clean = (value: any) => {
    if (value === null || value === undefined) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    const lower = trimmed.toLowerCase();
    if (lower === "null" || lower === "undefined" || lower === "n/a" || lower === "na") return "";
    return trimmed;
};

const cardSx = {
    flex: 1,
    border: "1px solid #dfeee6",
    borderRadius: 3,
    p: 3,
    background: "#f7fffb",
    boxShadow: "0 8px 24px rgba(47,191,113,0.18)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, #2fbf71 0%, #27a864 100%)"
    }
};

const iconBadgeSx = (bg: string, color: string) => ({
    width: 40,
    height: 40,
    borderRadius: 2,
    bgcolor: bg,
    color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InfoRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: any }) => {
    const cleaned = clean(value);
    if (!cleaned) return null;
    return (
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            {Icon ? <Icon sx={{ fontSize: 18, color: "#5b6778", mt: 0.25, flexShrink: 0 }} /> : null}
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...labelSx, mb: 0.25 }}>{label}</Typography>
                <Typography sx={{ ...valueSx, wordBreak: "break-word" }}>{cleaned}</Typography>
            </Box>
        </Stack>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PatientCard = ({ patient }: { patient: any }) => {
    const name = clean(patient?.patientName) || "—";
    const gender = clean(patient?.patientGender);
    const dob = clean(patient?.patientBirthDate);
    const phone = clean(patient?.patientPhone);
    return (
        <Box sx={cardSx}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={iconBadgeSx("#d3f0e9", "#2d6a4f")}>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ ...sectionTitleSx, mb: 0, fontSize: 15 }}>Patient</Typography>
            </Stack>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    color: "#1f3a2e",
                    mb: 1.5,
                    wordBreak: "break-word"
                }}
            >
                {name}
            </Typography>
            <Stack spacing={1.25}>
                {gender ? <InfoRow icon={WcOutlinedIcon} label="Gender" value={gender} /> : null}
                {dob ? (
                    <InfoRow icon={CakeOutlinedIcon} label="Date of Birth" value={formatDateOnly(dob)} />
                ) : null}
                {phone ? <InfoRow icon={PhoneOutlinedIcon} label="Phone" value={phone} /> : null}
                {!gender && !dob && !phone ? (
                    <Typography sx={{ ...labelSx, fontStyle: "italic" }}>
                        No additional patient details available.
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PractitionerCard = ({ practitioner }: { practitioner: any }) => {
    const name = clean(practitioner?.practitionerName) || "—";
    const license = clean(practitioner?.practitionerLicenseNumber);
    const specialty = clean(practitioner?.practitionerSpecialty);
    return (
        <Box sx={cardSx}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={iconBadgeSx("#e0e7ff", "#5c6bc0")}>
                    <MedicalInformationOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ ...sectionTitleSx, mb: 0, fontSize: 15 }}>Practitioner</Typography>
            </Stack>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    color: "#1f3a2e",
                    mb: 1.5,
                    wordBreak: "break-word"
                }}
            >
                {name}
            </Typography>
            <Stack spacing={1.25}>
                {license ? <InfoRow icon={BadgeOutlinedIcon} label="Registration / License" value={license} /> : null}
                {specialty ? <InfoRow icon={null} label="Specialty" value={specialty} /> : null}
                {!license && !specialty ? (
                    <Typography sx={{ ...labelSx, fontStyle: "italic" }}>
                        No additional practitioner details available.
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
};

// HIP / Hospital banner shown prominently at the top of each bundle view.
// The organizationName takes centre stage; identifier/type/contact appear as
// smaller metadata so the doctor immediately knows WHICH hospital issued the
// record instead of having to guess from a numeric HIP id.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HospitalBanner = ({ organization }: { organization: any }) => {
    const name = clean(organization?.organizationName);
    const identifier = clean(organization?.organizationIdentifierValue);
    const type = clean(organization?.organizationType);
    const contact = clean(organization?.organizationContact);
    const address = clean(organization?.organizationAddress);

    if (!name && !identifier) return null;

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                background: "linear-gradient(135deg, #2fbf71 0%, #27a864 100%)",
                color: "#ffffff",
                boxShadow: "0 12px 32px rgba(47,191,113,0.28)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 160,
                    height: 160,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)"
                }
            }}
        >
            <Stack direction="row" spacing={2.5} alignItems="center" sx={{ position: "relative" }}>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2.5,
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        border: "1.5px solid rgba(255,255,255,0.35)"
                    }}
                >
                    <LocalHospitalOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: 1.4,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            opacity: 0.85,
                            mb: 0.5
                        }}
                    >
                        Health Information Provider
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                            fontWeight: 800,
                            lineHeight: 1.2,
                            wordBreak: "break-word"
                        }}
                    >
                        {name || "Unnamed facility"}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 1 }}
                    >
                        {type ? (
                            <Chip
                                size="small"
                                label={type}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    color: "#ffffff",
                                    fontWeight: 700,
                                    letterSpacing: 0.4,
                                    border: "1px solid rgba(255,255,255,0.35)"
                                }}
                            />
                        ) : null}
                        {identifier ? (
                            <Chip
                                size="small"
                                label={`HIP ID: ${identifier}`}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "#ffffff",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    border: "1px solid rgba(255,255,255,0.3)"
                                }}
                            />
                        ) : null}
                    </Stack>
                    {(contact || address) ? (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={{ xs: 0.5, sm: 2.5 }}
                            sx={{ mt: 1.25, opacity: 0.92 }}
                        >
                            {contact ? (
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{contact}</Typography>
                                </Stack>
                            ) : null}
                            {address ? (
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                    <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {address}
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Stack>
                    ) : null}
                </Box>
            </Stack>
        </Box>
    );
};

// Detailed organization section kept for compatibility with existing views
// that still render it below the banner. It no longer shows "—" rows when
// data is missing, and it highlights the name as the primary heading.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const OrganizationSection = ({ organization }: { organization: any }) => {
    const name = clean(organization?.organizationName);
    const identifier = clean(organization?.organizationIdentifierValue);
    const identifierType = clean(organization?.organizationIdentifierType);
    const type = clean(organization?.organizationType);
    const contact = clean(organization?.organizationContact);
    const address = clean(organization?.organizationAddress);
    const parent = clean(organization?.parentOrganization);

    const details = [
        { label: identifierType ? `${identifierType} ID` : "HIP Identifier", value: identifier, icon: BadgeOutlinedIcon },
        { label: "Organization Type", value: type, icon: null },
        { label: "Parent Organization", value: parent, icon: null },
        { label: "Contact", value: contact, icon: PhoneOutlinedIcon },
        { label: "Address", value: address, icon: PlaceOutlinedIcon }
    ].filter((row) => clean(row.value));

    if (!name && details.length === 0) return null;

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                border: "2px solid #f0f0f0",
                bgcolor: "#ffffff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "5px",
                    background: "linear-gradient(180deg, #2fbf71 0%, #27a864 100%)",
                    borderRadius: "3px 0 0 3px"
                }
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: 1, mb: 2 }}>
                <Box sx={iconBadgeSx("#d3f0e9", "#2d6a4f")}>
                    <LocalHospitalOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Organization</Typography>
            </Stack>
            {name ? (
                <Typography
                    sx={{
                        fontSize: "1.15rem",
                        fontWeight: 800,
                        color: "#1f3a2e",
                        ml: 1,
                        mb: 2,
                        wordBreak: "break-word"
                    }}
                >
                    {name}
                </Typography>
            ) : null}
            {details.length > 0 ? (
                <Box
                    sx={{
                        ml: 1,
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }
                    }}
                >
                    {details.map((row) => (
                        <InfoRow key={row.label} icon={row.icon} label={row.label} value={row.value} />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
};
